/**
 * The saved-plan Swap write, shared by the Swap browser (Meals.jsx) and a
 * candidate's detail screen (MealView.jsx) so opening a candidate's detail
 * during a Swap is still the SAME Swap — never a plain add that leaves the
 * outgoing row in place.
 *
 *   linked leftover child  -> the atomic child-only replacement endpoint
 *   ordinary Swap          -> delete the outgoing row, then add the incoming
 *                             recipe with source 'swap' (the backend checks
 *                             it is a published recipe; a same-day repeat,
 *                             Don't Suggest or allergen conflict is the
 *                             user's manual choice and is not rejected)
 *
 * Taste signals are recorded only after the write succeeds.
 */
import { mealPlanAPI } from './api';
import { getTodayString } from './dateUtils';
import { recordSwapTasteSignals } from './tasteSignals';
import { recordDraftSwapPick } from './draftSwapHandoff';

/**
 * The Swap context carried on a Swap URL, or null when the URL is not a Swap.
 * A Meal Review Swap (`draftSwap=1`) also carries the draft row it replaces
 * (`isDraft`, `draftRowKey`); a Plan Swap's context is unchanged.
 */
export function readSwapContext(searchParams) {
  if (searchParams.get('swap') !== 'true') return null;
  var ctx = {
    replacePlanId: searchParams.get('replacePlanId') || '',
    isLinkedChild: searchParams.get('linkedChild') === '1',
    expectedMealId: searchParams.get('expectedMealId') || '',
    outgoingMealId: searchParams.get('outgoingMealId') || '',
  };
  if (searchParams.get('draftSwap') === '1') {
    ctx.isDraft = true;
    ctx.draftRowKey = searchParams.get('draftRowKey') || '';
  }
  return ctx;
}

/** The Swap context as URL params, to hand to a candidate's detail screen. */
export function swapContextParams(ctx) {
  if (!ctx) return {};
  var params = {
    swap: 'true',
    replacePlanId: ctx.replacePlanId,
    outgoingMealId: ctx.outgoingMealId,
    linkedChild: ctx.isLinkedChild ? '1' : '',
    expectedMealId: ctx.isLinkedChild ? ctx.expectedMealId : '',
  };
  if (ctx.isDraft) {
    params.draftSwap = '1';
    params.draftRowKey = ctx.draftRowKey;
  }
  return params;
}

/**
 * Perform the Swap. args = { date, mealType, meal: { meal_id, name } }.
 * Resolves when the plan has been written; rejects with the API error.
 *
 * Meal Review Swap (ctx.isDraft): NO meal-plan write at all -- the choice is
 * handed back to the unsaved Review draft (utils/draftSwapHandoff), which is
 * saved only by Confirm & Save. Its taste signals are recorded once the
 * choice has been handed back, as an applied draft Swap always did.
 */
export function performSwap(ctx, args) {
  var meal = args.meal;
  if (ctx.isDraft) {
    return recordDraftSwapPick(ctx.draftRowKey, meal).then(function () {
      recordSwapTasteSignals(ctx.outgoingMealId, meal.meal_id);
    });
  }
  if (ctx.isLinkedChild && ctx.replacePlanId) {
    var opId = 'replchild_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    return mealPlanAPI.replaceLeftoverChild(ctx.replacePlanId, meal.meal_id, {
      clientToday: getTodayString(),
      expectedMealId: ctx.expectedMealId || null,
      operationId: opId,
    }).then(function () {
      recordSwapTasteSignals(ctx.expectedMealId, meal.meal_id);
    });
  }

  var deletePromise = ctx.replacePlanId ? mealPlanAPI.deleteMealPlan(ctx.replacePlanId) : Promise.resolve();
  return deletePromise
    .then(function () {
      return mealPlanAPI.addMealPlan({
        date: args.date,
        meal_type: args.mealType,
        meal_id: meal.meal_id,
        meal_name: meal.name,
        source: 'swap',
      });
    })
    .then(function () {
      recordSwapTasteSignals(ctx.outgoingMealId, meal.meal_id);
    });
}

/** User-facing message for a failed Swap (the copy the Swap browser always used). */
export function swapErrorMessage(error, ctx) {
  if (ctx && ctx.isDraft) {
    var draftCode = error && error.draftSwapCode;
    if (draftCode === 'allergen_conflict') return "This meal conflicts with your allergies, so it can't be saved in this plan.";
    if (draftCode === 'duplicate_meal') return 'This meal is already planned in another meal slot on this day.';
    return "Couldn't swap this meal. Go back to Review Plan and try again.";
  }
  if (ctx && ctx.isLinkedChild && ctx.replacePlanId) {
    var detail = error && error.response && error.response.data && error.response.data.detail;
    var code = (detail && (detail.error || detail)) || '';
    if (code === 'active_slot_conflict') return 'There is already a meal in that slot for this day.';
    if (code === 'use_replace_both') return 'This pair is still active — use Replace both instead.';
    if (code === 'child_completed' || code === 'already_replaced') return 'This leftover can no longer be swapped.';
  }
  return 'Failed to swap meal';
}
